import { z } from "zod";
// The contract of location and time-zone evidence for itinerary planning (CHT-743; contract
// `docs/architecture/milestone-d-contracts.md`, sections 4.2 and 4.5). Same rules as the other schema files: every
// object is STRICT (an unknown key is a failure, so a coordinate or a Place ID cannot ride along), the API validates
// bodies with these schemas, and the clients validate responses with them.
//
// STORAGE POLICY AND LEAK RULE. A place candidate carries `{ placeRef, label, locality, countryCode,
// suggestedTimeZone }` and nothing else: no Place ID, no coordinate, no address. The `label`, the locality, the
// country and the zone suggestion are provider content that lives in one HTTP response (`Cache-Control: no-store`).
// The model passes a `placeRef` back together with the customer's own words for the name; it never types a Place ID.
//
// This package cannot import the domain package. The tuples and bounds below repeat the domain's
// (`itinerary-location.ts`, `place-ref.ts`); `apps/api` has a parity test that fails when one differs.
// --- codes (parity-tested against the domain) --------------------------------------------------------------
export const locationDecisionCodes = ["RESOLVED", "AMBIGUOUS", "UNRESOLVED"];
export const locationSourceCodes = ["CATALOG", "STAFF", "PLACES_REFERENCE"];
/** The sources an item's zone may be stored with. A zone that Places reports is never one of them. */
export const zoneConfirmationSourceCodes = ["CATALOG", "STAFF", "CUSTOMER"];
export const locationEvidenceFieldSetCodes = ["SEARCH_CANDIDATES_V1", "PLACE_PLANNING_V1"];
export const locationConfirmerKindCodes = ["NONE", "CUSTOMER", "STAFF"];
export const placeLookupUnavailableCodes = [
    "NOT_CONFIGURED",
    "TIMEOUT",
    "RATE_LIMITED",
    "QUOTA_EXHAUSTED",
    "NOT_FOUND",
    "INVALID_REQUEST",
    "PERMISSION_DENIED",
    "UNAVAILABLE",
];
export const attachPlaceResultCodes = [
    "ATTACHED",
    "PLACE_REF_EXPIRED",
    "CLARIFICATION_REQUIRED",
    "CATALOG_ITEM_HAS_OWN_LOCATION",
    "ITEM_KIND_NOT_SUPPORTED",
    "HUMAN_CONFIRMATION_REQUIRED",
];
export const confirmZoneResultCodes = [
    "CONFIRMED",
    "ZONE_CHANGE_NEEDS_EDIT",
    "ZONE_NOT_FOR_CATALOG_ITEM",
    "CATALOG_ZONE_UNKNOWN",
    "HUMAN_CONFIRMATION_REQUIRED",
];
export const ITINERARY_LOCATION_BOUNDS = Object.freeze({
    textMax: 200,
    nameMax: 200,
    localityMax: 100,
    candidatesMax: 5,
    placeRefPattern: /^pr_[A-Za-z0-9_-]{22,64}$/,
    timeZoneShape: /^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+){1,2})$/,
});
// --- shared fields -----------------------------------------------------------------------------------------
const uuid = z.string().uuid();
// Visible text only: no control character, at least one character.
const NO_CONTROL = /^[^\p{Cc}]+$/u;
const boundedText = (max) => z.string().trim().min(1).max(max).regex(NO_CONTROL, "must not contain control characters");
export const placeRefSchema = z.string().regex(ITINERARY_LOCATION_BOUNDS.placeRefPattern);
export const ianaTimeZoneTextSchema = z
    .string()
    .max(64)
    .regex(ITINERARY_LOCATION_BOUNDS.timeZoneShape);
const countryCode = z.string().regex(/^[A-Z]{2}$/);
const expectedVersion = z.number().int().min(1);
// --- the lookup (the use case behind the `lookupPlace` tool and the staff route) ------------------------------
/** One candidate. No Place ID, no coordinate, no address: an unknown key fails. */
export const placeCandidateViewSchema = z.strictObject({
    placeRef: placeRefSchema,
    label: boundedText(ITINERARY_LOCATION_BOUNDS.nameMax),
    locality: boundedText(ITINERARY_LOCATION_BOUNDS.localityMax).nullable(),
    countryCode: countryCode.nullable(),
    suggestedTimeZone: ianaTimeZoneTextSchema.nullable(),
});
/** The staff request of `POST /staff/itinerary-places/resolve`. */
export const resolvePlaceCandidatesBodySchema = z.strictObject({
    customerId: uuid,
    text: boundedText(ITINERARY_LOCATION_BOUNDS.textMax),
    regionCode: countryCode.optional(),
    /** The stable id of the item the lookup is for. An ambiguous or empty answer is then written as evidence. */
    lineageId: uuid.optional(),
});
export const resolvePlaceCandidatesResultSchema = z.discriminatedUnion("kind", [
    z.strictObject({
        kind: z.literal("CANDIDATES"),
        decision: z.enum(["RESOLVED", "AMBIGUOUS"]),
        candidates: z
            .array(placeCandidateViewSchema)
            .min(1)
            .max(ITINERARY_LOCATION_BOUNDS.candidatesMax),
        /** `true`: ask the customer which place they mean before anything is attached. */
        needsClarification: z.boolean(),
    }),
    z.strictObject({
        kind: z.literal("NO_CANDIDATES"),
        decision: z.literal("UNRESOLVED"),
        candidates: z.array(placeCandidateViewSchema).length(0),
        needsClarification: z.literal(true),
    }),
    z.strictObject({
        kind: z.literal("UNAVAILABLE"),
        code: z.enum(placeLookupUnavailableCodes),
    }),
]);
// --- attach and confirm ---------------------------------------------------------------------------------------
/**
 * The body of an attach by the app or by staff. `confirmAmbiguousChoice` is for the customer's or staff's own
 * choice out of an ambiguous list; a tool must use `attachPlaceToItemToolInputSchema`, which has no such field.
 */
export const attachPlaceToItemBodySchema = z.strictObject({
    itineraryId: uuid,
    itemId: uuid,
    expectedVersion,
    placeRef: placeRefSchema,
    /** The words the customer or staff typed. Never the provider's display name. */
    name: boundedText(ITINERARY_LOCATION_BOUNDS.nameMax),
    confirmAmbiguousChoice: z.boolean().optional(),
});
/** What a model may pass: a `placeRef`, the customer's words, and the item. It cannot confirm an ambiguous choice. */
export const attachPlaceToItemToolInputSchema = attachPlaceToItemBodySchema.omit({
    confirmAmbiguousChoice: true,
});
export const attachPlaceResultSchema = z.discriminatedUnion("result", [
    z.strictObject({
        result: z.literal("ATTACHED"),
        itineraryVersion: z.number().int().min(1),
        /** `true` while the item has no confirmed zone: it cannot be scheduled yet. */
        needsZoneConfirmation: z.boolean(),
        /** `ITEM_ZONE_LOCATION_MISMATCH`: the item's zone differs from the zone the provider suggested for this place. */
        zoneMismatch: z.boolean(),
    }),
    z.strictObject({ result: z.literal("PLACE_REF_EXPIRED") }),
    z.strictObject({ result: z.literal("CLARIFICATION_REQUIRED") }),
    z.strictObject({ result: z.literal("CATALOG_ITEM_HAS_OWN_LOCATION") }),
    z.strictObject({ result: z.literal("ITEM_KIND_NOT_SUPPORTED") }),
    z.strictObject({ result: z.literal("HUMAN_CONFIRMATION_REQUIRED") }),
]);
export const confirmItemTimeZoneBodySchema = z.strictObject({
    itineraryId: uuid,
    itemId: uuid,
    expectedVersion,
    /** `null` for a catalog item: it uses the zone of its catalog item. */
    timeZone: ianaTimeZoneTextSchema.nullable(),
    /** The candidate this zone was chosen for; compared with its transient suggestion for `zoneMismatch`. */
    placeRef: placeRefSchema.optional(),
});
export const confirmItemTimeZoneResultSchema = z.discriminatedUnion("result", [
    z.strictObject({
        result: z.literal("CONFIRMED"),
        itineraryVersion: z.number().int().min(1),
        zoneSource: z.enum(zoneConfirmationSourceCodes),
        change: z.enum(["SET", "RECONFIRMED", "CHANGED"]),
        zoneMismatch: z.boolean(),
    }),
    z.strictObject({ result: z.literal("ZONE_CHANGE_NEEDS_EDIT") }),
    z.strictObject({ result: z.literal("ZONE_NOT_FOR_CATALOG_ITEM") }),
    z.strictObject({ result: z.literal("CATALOG_ZONE_UNKNOWN") }),
    z.strictObject({ result: z.literal("HUMAN_CONFIRMATION_REQUIRED") }),
]);
// --- reads -----------------------------------------------------------------------------------------------------
/** What a read tells about the location of one item. No place name, address, point or zone hint. */
export const itemLocationViewSchema = z.strictObject({
    itemId: uuid,
    stableId: uuid,
    source: z.enum(locationSourceCodes).nullable(),
    decision: z.enum(locationDecisionCodes).nullable(),
    evidenceObservedAt: z.string().datetime().nullable(),
    timeZone: ianaTimeZoneTextSchema.nullable(),
    timeZoneSource: z.enum(zoneConfirmationSourceCodes).nullable(),
    needsZoneConfirmation: z.boolean(),
});
export const itineraryLocationReportSchema = z.strictObject({
    itineraryId: uuid,
    items: z.array(itemLocationViewSchema).max(1000),
    issues: z
        .array(z.strictObject({
        code: z.enum(["LOCATION_UNRESOLVED", "LOCATION_AMBIGUOUS", "ITEM_ZONE_LOCATION_MISMATCH"]),
        severity: z.enum(["BLOCKING", "WARNING"]),
        itemId: uuid.nullable(),
    }))
        .max(1000),
});
/**
 * One row of location evidence, as staff may read it: the Place ID is allowed (it is the one thing Lafayette
 * stores), and there is no name, address, coordinate, zone, locality, country or search text.
 */
export const locationEvidenceViewSchema = z.strictObject({
    lineageId: uuid,
    observedAt: z.string().datetime(),
    provider: z.literal("google_places"),
    placeId: z.string().min(1).max(255).nullable(),
    fieldSet: z.enum(locationEvidenceFieldSetCodes),
    decision: z.enum(locationDecisionCodes),
    candidateCount: z.number().int().min(0).max(5),
    confirmedBy: z.enum(locationConfirmerKindCodes),
    confirmedByStaffUserId: uuid.nullable(),
});
export const STAFF_ITINERARY_PLACE_RESOLVE_PATH = "/staff/itinerary-places/resolve";
